import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { isOAuthTokenUnavailable } from "../errorUtils";
import { QUERY_KEYS } from "../queryKeys";
import { getCalendarConnection } from "../utils/calendarConnection";
import { useCalendarSettings } from "./useCalendarSettings";
interface CalendarEventOptions {
  refetchInterval?: number;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
  source?: string;
  /**
   * `yyyy-MM-dd`: the week starting that day, in the member's zone (the
   * calendar grid, TX-5433). Without it the read is today to +10 days.
   */
  weekStart?: string;
}
interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * True when the last fetch said the member must reconnect before anything can
 * succeed: the provider token is gone, or it lacks a scope we need.
 *
 * The UI already shows a reconnect button for exactly these, and reconnecting
 * invalidates the events query — so polling in the meantime only produces
 * requests that cannot work (a 401 every five minutes, all day, per member).
 */
function needsReconnect(error: unknown): boolean {
  return isOAuthTokenUnavailable(error) || (error as {
    code?: string;
  } | null)?.code === "insufficient_scope";
}

/**
 * Fetches calendar events for the active user.
 * Defaults to Google if no source is provided.
 */
export function useCalendarEvents(options) {
  const $ = _c(27);
  const apiFetcher = useApiFetcher();
  const {
    data: calendarSettings,
    isLoading: isCalendarSettingsLoading,
    isError: isConnectionError,
    error: connectionError
  } = useCalendarSettings();
  let t0;
  if ($[0] !== calendarSettings) {
    t0 = getCalendarConnection(calendarSettings);
    $[0] = calendarSettings;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    hasCalendar,
    primarySource
  } = t0;
  const source = options?.source ?? primarySource ?? "google";
  const enabledByConnection = !isCalendarSettingsLoading && hasCalendar;
  const enabled = options?.enabled === true ? true : options?.enabled === false ? false : enabledByConnection;
  const weekStart = options?.weekStart;
  let t1;
  if ($[2] !== weekStart) {
    t1 = weekStart ? [weekStart] : [];
    $[2] = weekStart;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== source || $[5] !== t1) {
    t2 = [...QUERY_KEYS.CALENDAR_EVENTS, source, ...t1];
    $[4] = source;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== apiFetcher || $[8] !== source || $[9] !== weekStart) {
    t3 = () => apiFetcher(`/api/v1/user/calendar/${source}/events${weekStart ? `?weekStart=${encodeURIComponent(weekStart)}` : ""}`);
    $[7] = apiFetcher;
    $[8] = source;
    $[9] = weekStart;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== options?.refetchInterval) {
    t4 = q => needsReconnect(q.state.error) ? false : options?.refetchInterval ?? 300000;
    $[11] = options?.refetchInterval;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = options?.staleTime ?? 300000;
  const t6 = options?.gcTime ?? 600000;
  let t7;
  if ($[13] !== options?.refetchOnWindowFocus) {
    t7 = q_0 => needsReconnect(q_0.state.error) ? false : options?.refetchOnWindowFocus ?? true;
    $[13] = options?.refetchOnWindowFocus;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== enabled || $[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = {
      queryKey: t2,
      queryFn: t3,
      refetchInterval: t4,
      staleTime: t5,
      gcTime: t6,
      refetchOnWindowFocus: t7,
      enabled
    };
    $[15] = enabled;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const query = useQuery(t8);
  let t9;
  if ($[23] !== connectionError || $[24] !== isConnectionError || $[25] !== query) {
    t9 = Object.assign(query, {
      isConnectionError,
      connectionError: connectionError as Error | null
    });
    $[23] = connectionError;
    $[24] = isConnectionError;
    $[25] = query;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}

/** @deprecated Use useCalendarEvents instead */
export const useGoogleCalendarEvents = useCalendarEvents;
export function useCreateCalendarEvent(t0) {
  const $ = _c(8);
  const source = t0 === undefined ? "google" : t0;
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== apiFetcher || $[1] !== source) {
    t1 = event => apiFetcher(`/api/v1/user/calendar/${source}/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(event)
    });
    $[0] = apiFetcher;
    $[1] = source;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CALENDAR_EVENTS
      });
    };
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useMutation(t3);
}

/** @deprecated Use useCreateCalendarEvent instead */
export const useCreateGoogleCalendarEvent = useCreateCalendarEvent;
export function useUpdateCalendarEvent(t0) {
  const $ = _c(8);
  const source = t0 === undefined ? "google" : t0;
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== apiFetcher || $[1] !== source) {
    t1 = t2 => {
      const {
        eventId,
        event
      } = t2;
      return apiFetcher(`/api/v1/user/calendar/${source}/events/${eventId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(event)
      });
    };
    $[0] = apiFetcher;
    $[1] = source;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CALENDAR_EVENTS
      });
    };
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useMutation(t3);
}

/** @deprecated Use useUpdateCalendarEvent instead */
export const useUpdateGoogleCalendarEvents = useUpdateCalendarEvent;
export function useDeleteCalendarEvent(callbacks, t0) {
  const $ = _c(12);
  const source = t0 === undefined ? "google" : t0;
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== apiFetcher || $[1] !== source) {
    t1 = eventId => apiFetcher(`/api/v1/user/calendar/${source}/events/${eventId}`, {
      method: "DELETE"
    });
    $[0] = apiFetcher;
    $[1] = source;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== callbacks || $[4] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CALENDAR_EVENTS
      });
      callbacks?.onSuccess?.();
    };
    $[3] = callbacks;
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== callbacks) {
    t3 = error => {
      callbacks?.onError?.(error);
    };
    $[6] = callbacks;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useMutation(t4);
}

/** @deprecated Use useDeleteCalendarEvent instead */
export const useDeleteGoogleCalendarEvent = useDeleteCalendarEvent;